import { MassSystem } from '@/systems/MassSystem';
import { BrainSystem } from '@/systems/BrainSystem';
import { AbilitySystem } from '@/systems/AbilitySystem';
import { TentacleSystem } from '@/systems/TentacleSystem';
import { SentienceGate } from '@/systems/SentienceGate';
import { RelationResolver } from '@/systems/RelationResolver';
import { PerceptionSystem } from '@/systems/PerceptionSystem';
import { ThreatAuraSystem } from '@/systems/ThreatAuraSystem';
import { DnaSystem } from '@/systems/DnaSystem';
import { ConsumptionController } from '@/systems/ConsumptionController';
import type { ConsumptionConfig } from '@/systems/ConsumptionController';
import type { RelationOverride } from '@/systems/RelationResolver';
import type { AbilityUnlockRule } from '@/systems/AbilitySystem';
import { EventBus } from '@/core/EventBus';

/**
 * World-scoped container. Owns all the deep systems whose state persists
 * across scenes / arcs. Scenes bind to this; they don't construct new
 * systems.
 *
 * Construction is parameterized so individual arcs can seed initial state
 * (mass, brain, abilities unlocked) and install arc-specific rules
 * (relation overrides, unlock rules, species brain quality).
 */
export interface GameStateOptions {
  initialMass?: number;
  initialBrain?: number;
  relationOverrides?: RelationOverride[];
  abilityUnlockRules?: AbilityUnlockRule[];
  consumption: Pick<ConsumptionConfig, 'brainQualityFor' | 'consumeDurationMs'>;
  initialTentacles?: number;
}

export class GameState {
  readonly bus = new EventBus();
  readonly mass: MassSystem;
  readonly brain: BrainSystem;
  readonly abilities: AbilitySystem;
  readonly tentacles: TentacleSystem;
  readonly gate: SentienceGate;
  readonly relations: RelationResolver;
  readonly perception: PerceptionSystem;
  readonly threatAura: ThreatAuraSystem;
  readonly dna: DnaSystem;
  readonly consumption: ConsumptionController;

  private disposers: Array<() => void> = [];

  constructor(options: GameStateOptions) {
    this.mass = new MassSystem({ initialMass: options.initialMass ?? 0 });
    this.brain = new BrainSystem({ initialBrain: options.initialBrain ?? 0 });
    this.abilities = new AbilitySystem({ rules: options.abilityUnlockRules });
    this.tentacles = new TentacleSystem({ initialCount: options.initialTentacles });
    this.gate = new SentienceGate(this.brain);
    this.relations = new RelationResolver({ overrides: options.relationOverrides });
    this.perception = new PerceptionSystem();
    this.threatAura = new ThreatAuraSystem(this.mass);
    this.dna = new DnaSystem();
    this.consumption = new ConsumptionController(
      this.mass,
      this.brain,
      this.relations,
      this.abilities,
      {
        brainQualityFor: options.consumption.brainQualityFor,
        consumeDurationMs: options.consumption.consumeDurationMs,
        maxConcurrent: () => this.tentacles.count,
        durationMultiplier: () => 1,
      },
    );

    this.bridgeEvents();
  }

  /** Wire module-local events to the typed bus. */
  private bridgeEvents(): void {
    this.disposers.push(
      this.mass.onTierChange((tier, mass) => {
        this.bus.emit('mass:tier-change', { tier, mass });
      }),
    );
    this.disposers.push(
      this.brain.onLevelChange((value, level) => {
        this.bus.emit('brain:level-change', { level, value });
      }),
    );
    this.disposers.push(
      this.gate.onLevelChange((level) => {
        this.bus.emit('sentience:change', { level });
      }),
    );
    this.disposers.push(
      this.abilities.onUnlock((abilityId) => {
        this.bus.emit('ability:unlocked', { abilityId });
      }),
    );
    this.disposers.push(
      this.tentacles.onChange((count) => {
        this.bus.emit('tentacles:change', { count });
      }),
    );
    this.disposers.push(
      this.consumption.onEvent((e) => {
        if (e.type === 'start') {
          this.bus.emit('consume:start', { preyId: e.prey.id, speciesId: e.prey.speciesId });
        } else if (e.type === 'complete') {
          this.bus.emit('consume:complete', {
            preyId: e.prey.id,
            speciesId: e.prey.speciesId,
            biomassGained: e.biomassGained ?? 0,
            brainGained: e.brainGained ?? { quality: 'vermin', mass: 0 },
          });
        } else if (e.type === 'interrupted') {
          this.bus.emit('consume:interrupted', { preyId: e.prey.id, progress: e.progress });
        }
      }),
    );
  }

  dispose(): void {
    for (const d of this.disposers) d();
    this.disposers = [];
    this.gate.dispose();
    this.bus.clear();
  }
}
